import { writeQueryInterop } from '@confluence/apollo-relay-interop/entry-points/writeQuery.ts';
import type { QueryInteropFetchPolicy } from '@confluence/apollo-relay-interop/entry-points/mapFetchPolicyForQuery.ts';
import { waitForAllReturnWhenError } from '@confluence/query-preloader-tools/entry-points/waitForAllReturnWhenError.ts';
import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import {
	slowQueryGuard,
	SLOW_QUERY_DEFAULT_TIMEOUT,
} from '@confluence/query-preloader-tools/entry-points/slowQueryGuard.ts';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';
import { preloadFooterCreateButton } from '@confluence/create-blank-fabric-page/entry-points/preloadFooterCreateButton.ts';

import type {
	PageTreeQuery as PageTreeQueryType,
	PageTreeQueryVariables,
} from './queries/__types__/PageTreeQuery.ts';
import { INITIAL_PAGE_SIZE } from './paginationLimits.ts';
import type { GraphQLPageStatus } from './pageTreeStatuses.ts';
import PageTreeQuery from './queries/__generated__/PageTreeQuery.graphql.ts';
import PageTreeQueryWithSpaceKeyQuery from './queries/__generated__/PageTreeQueryWithSpaceKeyQuery.graphql.ts';
import type {
	PageTreeWithSpaceKeyQuery as PageTreeQueryWithSpaceKeyType,
	PageTreeWithSpaceKeyQueryVariables as PageTreeQueryWithSpaceKeyQueryVariables,
} from './queries/__types__/PageTreeWithSpaceKeyQuery.ts';

export type preloadPageTreeProps = {
	spaceKey: string;
	statuses: GraphQLPageStatus[];
	isSuperAdmin: boolean;
	contentId?: number | string;
	fetchPolicy?: QueryInteropFetchPolicy;
};

const validSpaceKey = /^~?[a-z0-9]+$/i;

export async function preloadPageTree({
	spaceKey,
	statuses,
	isSuperAdmin,
	contentId = '',
	fetchPolicy,
}: preloadPageTreeProps): Promise<any> {
	const pageTreeTimeout = StatsigConfigurations.getNumber(
		'confluence_frontend_preload_pagetree_timeout',
		SLOW_QUERY_DEFAULT_TIMEOUT,
	);

	return slowQueryGuard(
		'preloadPageTree',
		() =>
			waitForAllReturnWhenError([
				preloadPageTreeContent({ spaceKey, statuses, isSuperAdmin, contentId, fetchPolicy }),
				preloadFooterCreateButton(spaceKey),
			]),
		pageTreeTimeout,
	);
}

export async function preloadPageTreeContent({
	spaceKey,
	statuses,
	isSuperAdmin,
	contentId = '',
	fetchPolicy,
}: preloadPageTreeProps): Promise<void> {
	// only fetch using PageTreeQueryWithSpaceKeyQuery if contentId does not exist AND FF is enabled (only for SSR space overviews).
	// This is to differentiate between other use cases where contentId is not passed (according to prop types, it is an optional param)
	//skip preloading if we do not have a valid spacekey (we are likely redirecting)
	if (spaceKey && validSpaceKey.test(spaceKey) && !contentId) {
		const { data } = await query<
			PageTreeQueryWithSpaceKeyType,
			PageTreeQueryWithSpaceKeyQueryVariables
		>({
			query: PageTreeQueryWithSpaceKeyQuery,
			...(fetchPolicy && { fetchPolicy }),
			variables: {
				spaceKey,
				isSuperAdmin,
				statuses,
				// @ts-ignore
				ptStatuses: statuses,
				paginationLimit: INITIAL_PAGE_SIZE,
			},
		});

		if (data?.space?.homepageId && data?.space?.id) {
			writeQueryInterop<PageTreeQueryType, PageTreeQueryVariables>({
				query: PageTreeQuery,
				variables: {
					spaceKey,
					isSuperAdmin,
					statuses,
					// @ts-ignore
					ptStatuses: statuses,
					contentId: data?.space?.homepageId,
					paginationLimit: INITIAL_PAGE_SIZE,
				},
				data: {
					currentPage: data?.space?.pageTree || null,
					ptpage: data?.ptpage || null,
					space: data?.space
						? {
								id: data?.space?.id,
								homepage: {
									id: data?.space?.homepageId,
									// @ts-ignore
									__typename: 'Content',
								},
								operations: data?.space?.operations,
								// @ts-ignore
								__typename: 'Space',
							}
						: null,
				},
			});
		}
		//skip preloading if we do not have a valid spacekey (we are likely redirecting)
	} else if (spaceKey && validSpaceKey.test(spaceKey)) {
		await query({
			query: PageTreeQuery,
			...(fetchPolicy && { fetchPolicy }),
			variables: {
				spaceKey,
				isSuperAdmin,
				statuses,
				ptStatuses: statuses,
				...(contentId && { contentId }),
				paginationLimit: INITIAL_PAGE_SIZE,
			},
		});
	}
}
